import { BaseSymbol } from '@ui/assets/config/trade-pair-listings';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { Order } from '@ui/hooks/trade/useTradeOrders/types';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Button } from '@ui/lib/park/ui/button';
import { Text } from '@ui/lib/park/ui/text';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { ReactNode, useMemo, useState } from 'react';
import { css } from 'styled-system/css';
import {
  Box,
  Flex,
  Grid,
  HStack,
  Stack,
  styled,
  VStack,
} from 'styled-system/jsx';

import { useTradePairInfosByPairBase } from '../../../../hooks/trade/useTradePairInfos';
import { useGetExecutionFeeUsd } from '../../hooks/useGetExecutionFee';
import { useTradingPair } from '../../store/useTradingPair';
import { LeverageBadge } from '../LeverageBadge';
import { getSidePillProps } from '../PositionList/styles';
import { PairSideCell, SidePill } from '../shared/PairSideCell';
import { Row } from '../Row';
import { CancelButton } from './CancelButton';
import { OrderListContextProvider, useOrderListContext } from './context';
import { CurrentTradingPairToggle } from './CurrentTradingPairToggle';
import { useLeverage } from './hooks/useLeverage';
import { useLiqLostP } from './hooks/useLiqLostP';
import { LimitOrderAt } from './LimitOrderAt';
import { OrderMarketCloseIndicator } from './OrderMarketCloseIndicator';
import { OrderTpSlEditButton } from './OrderTpSlEditButton';

const getFilterIsCurrentPair = (baseSymbol: BaseSymbol) => (item: Order) => {
  return item.pair.baseSymbol === baseSymbol;
};
export const OrderList = ({
  orders,
  emptyView,
  showCurrentPairToggle = true,
}: {
  orders: Order[];
  emptyView: ReactNode;
  showCurrentPairToggle?: boolean;
}) => {
  const { t } = useTranslation();
  const [isCurrentPairOnly, setIsCurrentPairOnly] = useState(false);
  const tradingPair = useTradingPair((v) => v.useTradingPair());
  const filteredOrders = useMemo(() => {
    const filters: ((item: Order) => boolean)[] = [];
    if (isCurrentPairOnly) {
      filters.push(getFilterIsCurrentPair(tradingPair.baseSymbol));
    }

    return filters.reduce((acc, filter) => acc.filter(filter), orders);
  }, [isCurrentPairOnly, orders, tradingPair.baseSymbol]);

  return (
    <>
      {showCurrentPairToggle && (
        <CurrentTradingPairToggle
          css={{
            justifyContent: 'end',
            height: '0',
            // float into the 40px V2 tab bar, vertically centered
            transform: 'translateY(-20px) translateX(-10px)',
          }}
          display={{ base: 'none', desktop: 'flex' }}
          checked={isCurrentPairOnly}
          onCheckedChange={(v) => {
            setIsCurrentPairOnly(v.checked);
          }}
        />
      )}
      <Grid
        gridTemplateColumns="minmax(200px, 20fr) minmax(100px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(100px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr)"
        columnGap={0}
        rowGap={0}
        px={4}
        display={{ base: 'none', desktop: 'grid' }}
        h="32px"
        alignItems="center"
        borderBottom="1px solid"
        bg="v2.bg.app"
        borderBottomColor="v2.border.hairline"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Stack gap={1}>
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Pair & Side
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Limit
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Size
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Collateral In
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Market
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            Liquidation
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            SL
          </Text>
        </Stack>
        <Stack gap={1} alignItems="flex-start">
          <Text textStyle="v2.tableHeader" color="v2.text.subtle">
            TP
          </Text>
        </Stack>
      </Grid>
      <Stack gap="0" px={{ base: '10px', desktop: 0 }} pt={{ base: 2, desktop: 0 }}>
        {filteredOrders.map((order) => (
          <OrderListContextProvider pair={order.pair} key={order.id}>
            <OrderCard order={order} />
            <OrderCardMobile order={order} />
          </OrderListContextProvider>
        ))}
      </Stack>
      {filteredOrders.length === 0 && emptyView}
    </>
  );
};

const OrderCard = ({ order }: { order: Order }) => {
  const { t } = useTranslation();
  const priceDisplayDecimals = useTokenDisplayDecimals(
    order?.pair.baseSymbol,
    order?.pair.priceDisplayDecimals,
  );

  return (
    <Flex
      data-cy={`OrderCard-${order.isLong ? 'Long' : 'Short'}-${
        order.pair.address
      }`}
      data-cy-id={order.id}
    >
      <OrderMarketCloseIndicator mb={3} />
      <Grid
        gridTemplateColumns="minmax(200px, 20fr) minmax(100px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(100px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr) minmax(60px, 10fr)"
        columnGap={0}
        rowGap={0}
        px={4}
        py={2}
        display={{ base: 'none', desktop: 'grid' }}
        alignItems="center"
        borderBottom="1px solid"
        borderBottomColor="v2.border.hairline"
        w="full"
        transition="background 120ms"
        _hover={{ bg: 'rgba(255, 255, 255, 0.035)' }}
      >
        <PairSideCell
          image={order.pair.image}
          name={order.pair.baseName}
          pill={<OrderSidePill order={order} />}
        />
        <Stack gap="1" alignItems="flex-start">
          <LimitOrderAt
            order={order}
            labelStyle={labelStyle}
            valueStyle={valueStyle}
          />
        </Stack>
        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>{t('common.Size')}</Text> */}
          <AmountUsd
            css={valueStyle}
            value={order.sizeUsd}
            data-cy="SizeUsd"
            data-cy-value={order.sizeUsd}
          />
        </Stack>

        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>{t('common.Collateral')}</Text> */}
          <HStack gap={0.5} justifyContent="flex-start">
            <styled.img
              src={order.collateralToken.image}
              width="14px"
              height="14px"
            />
            <Collateral order={order} />
          </HStack>
        </Stack>

        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>{t('common.Mark')}</Text> */}
          <MarketPrice order={order} />
        </Stack>

        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>{t('common.Liquidation')}</Text> */}
          <LiqPrice order={order} />
        </Stack>

        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>SL</Text> */}
          <HStack justifyContent="flex-start">
            {order.stopLoss.isZero() ? (
              <Text css={valueStyle}>{t('common.Not Set')}</Text>
            ) : (
              <AmountUsd
                css={{ ...valueStyle, color: 'v2.short.default' }}
                value={order.stopLoss}
                opts={{
                  displayDecimals: priceDisplayDecimals,
                }}
                data-cy="SlPrice"
                data-cy-value={order.stopLoss}
              />
            )}
            <OrderTpSlEditButton order={order} />
          </HStack>
        </Stack>

        <Stack gap="1" alignItems="flex-start">
          {/* <Text css={labelStyle}>TP</Text> */}
          <HStack justifyContent="flex-start">
            <AmountUsd
              css={{ ...valueStyle, color: 'v2.long.default' }}
              value={order.takeProfit}
              opts={{
                displayDecimals: priceDisplayDecimals,
              }}
              data-cy="TpPrice"
              data-cy-value={order.takeProfit}
            />
            <OrderTpSlEditButton order={order} />
          </HStack>
        </Stack>
        <Stack gap="1" flexDir="row-reverse">
          <CancelButton order={order} />
        </Stack>
      </Grid>
    </Flex>
  );
};

const OrderCardMobile = ({ order }: { order: Order }) => {
  const { t } = useTranslation();
  const priceDisplayDecimals = useTokenDisplayDecimals(
    order?.pair.baseSymbol,
    order?.pair.priceDisplayDecimals,
  );

  return (
    <Box
      display={{ base: 'block', desktop: 'none' }}
      w="full"
      px="3"
      py="3"
      border="1px solid"
      borderColor="v2.border.default"
      bg="v2.bg.surface1"
      borderRadius="4px"
      mb="2"
    >
      {/* Header：Pair / Side / Leverage */}
      <HStack justifyContent="space-between" alignItems="center" mb="2">
        <HStack gap="2" alignItems="center">
          <styled.img src={order.pair.image} boxSize="6" borderRadius="6px" />
          <Text textStyle="v2.ui" color="v2.text.primary">
            {order.pair.baseName}
          </Text>
          <LongShortBadge
            ml={1}
            isLong={order.isLong}
            {...getSidePillProps(order.isLong)}
          />
          <Leverage order={order} />
        </HStack>
      </HStack>

      {/* Body：一行一个 label/value */}
      <Box borderTop="1px solid" borderTopColor="v2.border.hairline">
        <Row label={t('common.Limit')}>
          {/* 隐藏内部 label，只展示 value；沿用你原来的 props */}
          <LimitOrderAt
            order={order}
            labelStyle={{ display: 'none' }}
            valueStyle={valueStyle}
          />
        </Row>

        <Row label={t('common.Size')}>
          <AmountUsd css={valueStyle} value={order.sizeUsd} />
        </Row>

        <Row label={t('common.Collateral')}>
          <Collateral order={order} />
        </Row>

        <Row label={t('common.Mark')}>
          <MarketPrice order={order} />
        </Row>

        <Row label={t('common.Liquidation')}>
          <LiqPrice order={order} />
        </Row>

        <Row label="SL">
          <HStack>
            {order.stopLoss.isZero() ? (
              <Text css={valueStyle}>{t('common.Not Set')}</Text>
            ) : (
              <AmountUsd
                css={valueStyle}
                value={order.stopLoss}
                opts={{ displayDecimals: priceDisplayDecimals }}
              />
            )}
          </HStack>
        </Row>

        <Row label="TP">
          <HStack>
            <AmountUsd
              css={valueStyle}
              value={order.takeProfit}
              opts={{ displayDecimals: priceDisplayDecimals }}
            />
          </HStack>
        </Row>
      </Box>

      {/* Footer：Operations */}
      <HStack mt="3" justify="flex-end">
        <OrderTpSlEditButton order={order}>
          <Button variant="secondary" w="25%">
            {t('common.TP/SL')}
          </Button>
        </OrderTpSlEditButton>
        <Box w="25%">
          <CancelButton order={order} />
        </Box>
      </HStack>
    </Box>
  );
};

const LiqPrice = ({ order }: { order: Order }) => {
  const payTokenMarketPrice = usePayTokenPriceLatest(order.collateralToken);
  const executionFee = useGetExecutionFeeUsd();
  const openFeeP = useOrderListContext().feeConfig?.openFeeP;
  const lvTokenDiscountP = useOrderListContext().feeConfig?.lvTokenDiscountP;
  const liqLostP = useLiqLostP({ order });
  const priceDisplayDecimals = useTokenDisplayDecimals(
    order?.pair.baseSymbol,
    order?.pair.priceDisplayDecimals,
  );
  const tradePair = useTradePairInfosByPairBase(order.pair.address);

  const liqPrice = useMemo(() => {
    if (
      isNil(executionFee) ||
      isNil(openFeeP) ||
      isNil(lvTokenDiscountP) ||
      isNil(payTokenMarketPrice) ||
      isNil(tradePair) ||
      // for liqLostP, it depends on the mapped leverageMargin 's max Leverage, say 100x is max, so here if the position leverage is over 100x, then will be undefined
      // for APX, they has 0.05% deduction for positionSize to avoid this, that's how we do it, if still encounter liqLostP not mapped, think about:
      // 1 ) adjust the 0.05% to bigger threshold
      // 2) adding new leverageMargin in SC to support higher leverage
      isNil(liqLostP)
    )
      return undefined;

    return order.getLiquidationPrice({
      collateralMarketPrice: payTokenMarketPrice,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      liqLostP,
      tradePair,
    });
  }, [
    executionFee,
    lvTokenDiscountP,
    liqLostP,
    openFeeP,
    order,
    payTokenMarketPrice,
    tradePair,
  ]);


  return (
    <AmountUsd
      // liq price reads as risk — short family (V2 spec)
      css={{ ...valueStyle, color: 'v2.short.default' }}
      value={liqPrice}
      opts={{
        displayDecimals: priceDisplayDecimals,
      }}
      data-cy="LiqPrice"
      data-cy-value={liqPrice}
    />
  );
};

const MarketPrice = ({ order }: { order: Order}) => {
  const marketPrice = usePriceLive(order.pair.baseSymbol);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    order.pair.baseSymbol,
    order.pair.priceDisplayDecimals,
  );

  return (
    <AmountUsd
      css={valueStyle}
      value={marketPrice ?? 0}
      opts={{
        displayDecimals: priceDisplayDecimals,
      }}
      data-cy="MarketPrice"
      data-cy-value={marketPrice ?? 0}
    />
  );
};
const Leverage = ({ order }: { order: Order }) => {
  const leverage = useLeverage({ order });

  return <LeverageBadge leverage={leverage} isDegen={order.pair.isDegenPair} />;
};

/** unified side+leverage pill (#28) */
const OrderSidePill = ({ order }: { order: Order }) => {
  const leverage = useLeverage({ order });

  return <SidePill isLong={order.isLong} leverage={leverage} />;
};

const Collateral = ({
  order: { getCollateralUsd, collateralToken, pair },
}: {
  order: Order;
}) => {
  const collateralMarketPrice = usePayTokenPriceLatest(collateralToken);
  const executionFee = useGetExecutionFeeUsd();
  const pariInfo = useTradePairInfosByPairBase(pair.address);
  const openFeeP = pariInfo?.feeConfig?.openFeeP;
  const lvTokenDiscountP = pariInfo?.feeConfig?.lvTokenDiscountP;

  const collateral = useMemo(() => {
    if (
      !collateralMarketPrice ||
      !lvTokenDiscountP ||
      !executionFee ||
      !openFeeP
    )
      return;

    return getCollateralUsd({
      collateralMarketPrice,
      executionFee,
      lvTokenDiscountP,
      openFeeP,
    });
  }, [
    collateralMarketPrice,
    lvTokenDiscountP,
    executionFee,
    getCollateralUsd,
    openFeeP,
  ]);

  return (
    <AmountUsd
      css={valueStyle}
      value={collateral}
      data-cy="Collateral"
      data-cy-value={collateral}
    />
  );
};

const labelStyle = css.raw({
  textStyle: 'v2.meta',
  color: 'v2.text.muted',
});
const valueStyle = css.raw({
  textStyle: 'numbers.small',
  color: 'v2.text.secondary',
  w: 'fit-content', // allow tooltip to be centered
});
